// Shared helpers for the Blend Inventory scene.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import apiClient from '../../services/apiClient';
import { getCurrentTz, parseServerTs, zonedDayKey } from '../../timezone';

export const API = '/api/v1.0/blend';

/**
 * Best-effort GET for endpoints that may not exist yet (the WS3 backend ships
 * in parallel). Resolves to:
 *   { ok: true,  data }                       — loaded
 *   { ok: false, missing: true }              — endpoint 404s (not deployed yet)
 *   { ok: false, missing: false, error }      — real failure (network/5xx)
 * so callers can render a friendly empty state instead of blanking the page.
 */
export const fetchOr = async (path, params) => {
  try {
    const res = await apiClient.get(path, params ? { params } : undefined);
    return { ok: true, data: res.data };
  } catch (e) {
    return { ok: false, missing: e?.response?.status === 404, error: e };
  }
};

/** Unwrap list payloads defensively: the body may be the array itself or {key: [...]}. */
export const asList = (data, ...keys) => {
  if (Array.isArray(data)) return data;
  if (data && typeof data === 'object') {
    for (const k of keys) if (Array.isArray(data[k])) return data[k];
    for (const v of Object.values(data)) if (Array.isArray(v)) return v;
  }
  return [];
};

// ---------------------------------------------------------------------------
// Product policy (approved / tolerated / prohibited / needs_review)
// ---------------------------------------------------------------------------

export const POLICIES = [
  { key: 'approved', label: 'Approved', chipColor: 'success' },
  { key: 'tolerated', label: 'Tolerated', chipColor: 'default' },
  { key: 'prohibited', label: 'Prohibited', chipColor: 'error' },
  { key: 'needs_review', label: 'Needs review', chipColor: 'warning' },
  { key: 'ignored', label: 'Ignored', chipColor: 'default' },
];

export const policyMeta = (policy) => {
  const k = String(policy || '').toLowerCase().replace(/[\s-]/g, '_');
  return POLICIES.find((p) => p.key === k) || null;
};

// ---------------------------------------------------------------------------
// Per-host activity level from VerOps Scout (product detail host rows).
// 3 = focused foreground use, 2 = active, 1 = network-only, 0 = idle.
// ---------------------------------------------------------------------------

export const ACTIVITY_LEVELS = {
  3: { label: 'Focused', chipColor: 'success', hint: 'Foreground / focused use recently' },
  2: { label: 'Active', chipColor: 'info', hint: 'Process actively running' },
  1: { label: 'Network', chipColor: 'default', hint: 'Network activity only' },
  0: { label: 'Idle', chipColor: 'default', hint: 'Installed but idle' },
};

// ---------------------------------------------------------------------------
// Formatters
// ---------------------------------------------------------------------------

export const fmtBytes = (n) => {
  const v0 = Number(n);
  if (n == null || Number.isNaN(v0)) return '—';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let v = v0; let u = 0;
  while (v >= 1024 && u < units.length - 1) { v /= 1024; u += 1; }
  return `${v >= 100 || u === 0 ? Math.round(v) : v.toFixed(1)} ${units[u]}`;
};

/** "2026-07-16" from an ISO-ish timestamp; em-dash when absent/invalid. */
export const fmtDate = (when) => {
  if (!when) return '—';
  const s = String(when);
  return /^\d{4}-\d{2}-\d{2}/.test(s) ? s.slice(0, 10) : s;
};

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/** Today's "YYYY-MM-DD" in the resolved zone (user → org → browser). */
export const zonedToday = (tz = getCurrentTz()) => zonedDayKey(Date.now(), tz);

/**
 * "YYYY-MM-DD" calendar day of `when`. A date-only string (a term end, a
 * renewal date) IS its calendar day and is never shifted through an instant;
 * a timestamp is read in the resolved zone. '' when absent/unparseable.
 */
export const calendarDay = (when, tz = getCurrentTz()) => {
  if (when == null || when === '') return '';
  const s = String(when).trim();
  return DATE_ONLY.test(s) ? s : zonedDayKey(s, tz);
};

/** True when `when`'s calendar day is before today in the resolved zone —
 *  an end date is still current for the whole of that day. */
export const isPastDate = (when, tz = getCurrentTz()) => {
  const d = calendarDay(when, tz);
  return d !== '' && d < zonedToday(tz);
};

const epochDay = (ymd) => Date.UTC(+ymd.slice(0, 4), +ymd.slice(5, 7) - 1, +ymd.slice(8, 10)) / 86400000;

/**
 * Whole days from now until `when` (negative = past); null when absent. A
 * date-only value counts CALENDAR days from today in the resolved zone (0 on
 * the day itself); a timestamp counts whole 24h periods from now.
 */
export const daysUntil = (when, tz = getCurrentTz()) => {
  if (when == null || when === '') return null;
  const s = String(when).trim();
  if (DATE_ONLY.test(s)) return epochDay(s) - epochDay(zonedToday(tz));
  const t = parseServerTs(when);
  if (!Number.isFinite(t)) return null;
  return Math.floor((t - Date.now()) / 86400000);
};

/* ── Software categories ───────────────────────────────────────────────────
 * Category is FREE TEXT: nothing server-side whitelists it (BlendService's
 * SOFTWARE_PROPERTIES names the KEYS, never their values). This file used to
 * export a hardcoded closed list that matched neither the tenant's data nor the
 * platform's own vocabulary — a product categorised `infra` opened a Select
 * with no `infra` option, so it rendered blank and a save could clear it.
 *
 * The picker now offers GET /blend/categories — the tenant's in-use categories,
 * its normalization-rule catalog and its redundancy thresholds — and lets an
 * admin type a category of their own. There is NO curated constant left on
 * either side of the wire: the two it used to ship that the server never had
 * (`developer` against the catalog's `development`, `infrastructure` against
 * its `infra`) were the vocabulary fork the endpoint exists to prevent.
 */

/** Longest category we store — blend_products.category is varchar(100). */
export const CATEGORY_MAX_LENGTH = 100;

/**
 * THE canonical form of a category — MUST mirror
 * InventoryCategoryService.normalize exactly, or the value the UI shows after a
 * save is not the value the server stored.
 *
 * <p>Lower-cased, trimmed, every run of whitespace/underscore folded to one
 * hyphen (so "Infra", "infra " and "INFRA" are ONE category, and "Remote
 * Access" lands on the `remote-access` the rest of the platform uses), repeated
 * hyphens collapsed and edge hyphens stripped. Blank in, blank out: empty means
 * "no category".
 */
export const normalizeCategory = (raw) => {
  if (raw == null) return '';
  let s = String(raw).trim().toLowerCase();
  if (s === '' || s === 'null') return '';
  s = s.replace(/[\s_]+/g, '-').replace(/-{2,}/g, '-').replace(/^-+|-+$/g, '');
  return s.slice(0, CATEGORY_MAX_LENGTH);
};

/**
 * The option list for a category picker: the server catalog ∪ whatever is
 * ALREADY on the records being edited — de-duplicated by canonical value,
 * in-use first, then alphabetical (the order `groupBy` needs).
 *
 * <p>`currentValues` is the defect this fixes: a value on the record is always
 * an option, even when it appears in no list the server knows about.
 *
 * <p>MAY LEGITIMATELY RETURN AN EMPTY ARRAY — when the catalog request is still
 * in flight, when the backend predates the endpoint, or (in principle) for a
 * tenant whose vocabulary is genuinely empty. That is not a broken picker: the
 * field is free-solo, so an empty option list means "nothing to suggest, type
 * one", and CategoryField says exactly that rather than rendering a bare
 * dropdown. Padding it with a constant is what produced the fork this whole
 * catalog exists to undo.
 *
 * @param catalogRows rows from GET /blend/categories ({value, inUse}) or plain
 *                    strings; null/undefined while the request is in flight.
 * @param currentValues categories already set on the record(s) being edited.
 */
export const categoryOptions = (catalogRows, currentValues = []) => {
  const byValue = new Map();
  const add = (raw, inUse) => {
    const value = normalizeCategory(raw);
    if (!value) return;
    const prev = byValue.get(value);
    if (prev) { if (inUse) prev.inUse = true; return; }
    byValue.set(value, { value, inUse: !!inUse });
  };
  (Array.isArray(catalogRows) ? catalogRows : []).forEach((r) => {
    if (typeof r === 'string') add(r, false);
    else add(r?.value, !!r?.inUse);
  });
  (Array.isArray(currentValues) ? currentValues : []).forEach((c) => add(c, true));
  return [...byValue.values()].sort((a, b) => {
    if (a.inUse !== b.inUse) return a.inUse ? -1 : 1;
    return a.value.localeCompare(b.value);
  });
};

export const fmtMoney = (n) => (n == null ? '—' : `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`);
export const fmtNum = (n) => (n == null ? '—' : Number(n).toLocaleString());

/**
 * Why a device is on a searched Devices page when its SOFTWARE matched (#147):
 * "matched software: POWERPNT.EXE (Microsoft Office), …". Null when the row
 * matched on its own fields.
 */
export const matchedSoftwareLabel = (matches) => {
  if (!Array.isArray(matches) || matches.length === 0) return null;
  const parts = matches.map((m) => {
    const name = m?.name || '';
    const product = m?.product && m.product.toLowerCase() !== name.toLowerCase() ? ` (${m.product})` : '';
    const how = m?.relation === 'installed' ? ', installed' : '';
    return `${name}${product}${how}`;
  }).filter(Boolean);
  return parts.length ? `matched software: ${parts.join(', ')}` : null;
};

/* ── License compliance position (ITAM Wave 1 — F1) ── */

/** Position buckets from GET /blend/licenses/true-up and /licenses/{id}/position. */
export const POSITION_META = {
  compliant: { label: 'Compliant', tone: 'success' },
  over_deployed: { label: 'Over-deployed', tone: 'error' },
  under_utilized: { label: 'Under-utilized', tone: 'warning' },
  no_entitlements: { label: 'No entitlements', tone: 'default' },
};

export const ENTITLEMENT_TYPES = [
  { value: 'perpetual', label: 'Perpetual' },
  { value: 'subscription', label: 'Subscription' },
  { value: 'oem', label: 'OEM' },
  { value: 'trial', label: 'Trial' },
];

export const ENTITLEMENT_METRICS = [
  { value: 'per_device', label: 'Per device' },
  { value: 'per_user', label: 'Per user' },
  { value: 'per_core', label: 'Per core' },
  { value: 'site', label: 'Site' },
];

export const metricLabel = (m) =>
  (ENTITLEMENT_METRICS.find((x) => x.value === m) || {}).label || m || '—';

/** Minor units + ISO currency → display string ("1,234.50 USD"). */
export const fmtMinor = (minor, currency) => {
  if (minor == null) return '—';
  const major = Number(minor) / 100;
  const num = major.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return currency ? `${num} ${currency}` : num;
};

/* ── ITAM Wave 1 / F2 — OS patch posture ──────────────────────────────────
 * Bucket from GET /blend/patch-posture/summary and the `posture` field the
 * host rows now carry. `unknown` means nothing is being reported (old agent
 * or the patch-posture module is off), never "up to date".
 */

export const POSTURE_META = {
  current: { label: 'Current', tone: 'success' },
  behind: { label: 'Behind', tone: 'warning' },
  critical: { label: 'Critical', tone: 'error' },
  unknown: { label: 'Unknown', tone: 'default' },
};

export const postureMeta = (b) =>
  POSTURE_META[String(b || '').toLowerCase()] || POSTURE_META.unknown;

/* ── ITAM Wave 1 / F4 — asset import & physical asset tags ────────────────── */

/** Where an asset's record came from. `sys:origin` is absent on agent-created
 *  entities, so the backend defaults the read to "agent". */
export const ORIGIN_META = {
  agent: { label: 'Agent', tone: 'success' },
  import: { label: 'Imported', tone: 'info' },
};

/** What an import row would do / did. */
export const IMPORT_ACTION_META = {
  created: { label: 'Create', tone: 'success' },
  updated: { label: 'Update', tone: 'info' },
  skipped: { label: 'No change', tone: 'default' },
  error: { label: 'Error', tone: 'error' },
};

/** Canonical columns an asset CSV can carry (mirrors AssetImportService.ASSET_FIELDS). */
export const ASSET_IMPORT_FIELDS = [
  { value: 'assetTag', label: 'Asset tag', key: true },
  { value: 'hostName', label: 'Hostname', key: true },
  { value: 'serialNumber', label: 'Serial number', key: true },
  { value: 'manufacturer', label: 'Manufacturer' },
  { value: 'model', label: 'Model' },
  { value: 'osType', label: 'OS type' },
  { value: 'osVersion', label: 'OS version' },
  { value: 'custodian', label: 'Custodian' },
  { value: 'locationLabel', label: 'Location' },
  { value: 'cpuModel', label: 'CPU model' },
  { value: 'cpuCores', label: 'CPU cores' },
  { value: 'memoryTotalMb', label: 'Memory (MB)' },
];

/** Canonical columns a financials CSV can carry, plus its matching keys. */
export const FINANCIAL_IMPORT_FIELDS = [
  { value: 'assetTag', label: 'Asset tag', key: true },
  { value: 'serialNumber', label: 'Serial number', key: true },
  { value: 'hostName', label: 'Hostname', key: true },
  { value: 'purchaseDate', label: 'Purchase date' },
  { value: 'purchaseCost', label: 'Purchase cost' },
  { value: 'currency', label: 'Currency' },
  { value: 'vendor', label: 'Vendor' },
  { value: 'poRef', label: 'PO reference' },
  { value: 'invoiceRef', label: 'Invoice reference' },
  { value: 'warrantyStart', label: 'Warranty start' },
  { value: 'warrantyEnd', label: 'Warranty end' },
  { value: 'warrantyProvider', label: 'Warranty provider' },
  { value: 'depreciationMethod', label: 'Depreciation method' },
  { value: 'depreciationMonths', label: 'Depreciation months' },
  { value: 'salvageValue', label: 'Salvage value' },
];

/**
 * Canonical columns a vulnerability-advisory CSV can carry — mirrors
 * `VulnerabilityService.ADVISORY_FIELDS`. `product` and `cveId` are both
 * required (`key: true`): the server refuses an import without them, because an
 * advisory with no product cannot be matched and one with no identifier cannot
 * be looked up by the person who has to act on it.
 */
export const VULN_IMPORT_FIELDS = [
  { value: 'cveId', label: 'CVE / advisory ID', key: true },
  { value: 'product', label: 'Product', key: true },
  { value: 'vendor', label: 'Vendor' },
  { value: 'introducedVersion', label: 'First affected version' },
  { value: 'fixedVersion', label: 'Fixed in version' },
  { value: 'severity', label: 'Severity' },
  { value: 'cvssScore', label: 'CVSS score' },
  { value: 'ecosystem', label: 'Ecosystem' },
  { value: 'summary', label: 'Summary' },
  { value: 'referenceUrl', label: 'Reference URL' },
];

/* ── ITAM Wave 2 / G3 — vulnerability correlation ─────────────────────────── */

/** Severity vocabulary — the closed set from `VulnerabilityMatcher.SEVERITIES`. */
export const VULN_SEVERITY_META = {
  critical: { label: 'Critical', tone: 'error' },
  high: { label: 'High', tone: 'error' },
  medium: { label: 'Medium', tone: 'warning' },
  low: { label: 'Low', tone: 'info' },
  unknown: { label: 'Unknown', tone: 'default' },
};

/** Display order, worst first. Drives the filter chips and the KPI row. */
export const VULN_SEVERITIES = ['critical', 'high', 'medium', 'low', 'unknown'];

/**
 * Why the matcher refused to assert a match. Every one of these is a REFUSAL,
 * not a bug — surfacing them is the point, so each carries the plain-English
 * reason an admin needs to resolve it by hand.
 */
export const VULN_REASON_META = {
  no_version: {
    label: 'No version reported',
    hint: 'The agent found this software but the OS reported no version for it, so no range can be evaluated.',
  },
  unparseable_version: {
    label: 'Version not comparable',
    hint: 'The reported version is not dotted-numeric, so it cannot be compared with the advisory range.',
  },
  undecidable_range: {
    label: 'Advisory range unusable',
    hint: 'The advisory has no usable version range — an "affects all versions" claim is not asserted.',
  },
  version_scheme_mismatch: {
    label: 'Version scheme mismatch',
    hint: 'The name carries one version scheme and the reported version another (e.g. a build number against a marketing version), so the comparison would be meaningless.',
  },
  vendor_mismatch: {
    label: 'Vendor disagrees',
    hint: 'The installed publisher and the advisory vendor do not look like the same organisation — this may be a different product with the same name.',
  },
};

/** Per-feed status, from `VulnerabilityFeedProvider.STATUSES`. */
export const VULN_FEED_META = {
  ok: { label: 'Active', tone: 'success' },
  disabled: { label: 'Disabled', tone: 'default' },
  no_feed: { label: 'No feed', tone: 'warning' },
  error: { label: 'Error', tone: 'error' },
};

/* ── ITAM Wave 2 follow-ups — coverage + the needs-review workflow ────────── */

/**
 * The acknowledge / dismiss decisions an ADMIN (the person who runs this tenant,
 * not the operator who runs the platform) can record against a needs-review
 * finding. Mirrors `VulnerabilityReviewService.DECISIONS`.
 */
export const VULN_REVIEW_META = {
  acknowledged: {
    label: 'Acknowledged',
    tone: 'info',
    hint: 'Read and accepted as it stands — it stays on the record but leaves the queue.',
  },
  dismissed: {
    label: 'Dismissed',
    tone: 'default',
    hint: 'The match itself is wrong for this asset.',
  },
};

/** Reasons a decision can carry — mirrors `VulnerabilityReviewService.REASONS`. */
export const VULN_REVIEW_REASONS = [
  { value: 'false_match', label: 'Not this product', hint: 'A different product that happens to share the name.' },
  { value: 'not_applicable', label: 'Not applicable here', hint: 'The affected component is not present or not reachable on this asset.' },
  { value: 'mitigated', label: 'Mitigated', hint: 'A compensating control is in place.' },
  { value: 'accepted_risk', label: 'Risk accepted', hint: 'Understood and accepted for now.' },
  { value: 'duplicate', label: 'Duplicate', hint: 'Tracked under another finding.' },
  { value: 'other', label: 'Other', hint: 'Explain in the note.' },
];

export const vulnReviewReasonLabel = (v) =>
  VULN_REVIEW_REASONS.find((r) => r.value === v)?.label || String(v || '').replace(/_/g, ' ') || '—';

/** Review filter chips — the values `VulnerabilityService.REVIEW_FILTERS` accepts. */
export const VULN_REVIEW_FILTERS = [
  { value: 'all', label: 'Any review state' },
  { value: 'unreviewed', label: 'Unreviewed' },
  { value: 'acknowledged', label: 'Acknowledged' },
  { value: 'dismissed', label: 'Dismissed' },
];

/**
 * The one sentence the Vulnerabilities tab leads with.
 *
 * <p>An empty findings grid is ambiguous by construction — it means either "we
 * checked and found nothing" or "we had nothing to check against". This turns
 * the coverage numbers the last scan actually measured into that distinction,
 * in words. It never softens a gap: uncovered packages are named as uncovered.
 */
export const coverageSentence = (coverage) => {
  if (!coverage || !coverage.measured) {
    return 'Coverage hasn’t been measured yet — run a scan to find out how much of your installed software the loaded feeds can speak to.';
  }
  const total = Number(coverage.packagesTotal || 0);
  const uncovered = Number(coverage.packagesUncovered || 0);
  if (uncovered === 0) {
    return `Every one of your ${total.toLocaleString()} installed package${total === 1 ? '' : 's'} is covered by at least one loaded advisory feed.`;
  }
  if (uncovered === total) {
    return `No advisory in your loaded feeds covers any of your ${total.toLocaleString()} installed package${total === 1 ? '' : 's'} — an empty findings list below says nothing about your risk.`;
  }
  return `No advisory in your loaded feeds covers ${uncovered.toLocaleString()} of your ${total.toLocaleString()} installed packages.`;
};

/** Coverage share as a 0–1 fraction, or null when nothing has been measured. */
export const coverageRatio = (coverage) => {
  const total = Number(coverage?.packagesTotal || 0);
  if (!coverage?.measured || total <= 0) return null;
  return Number(coverage.packagesCovered || 0) / total;
};

/* ── ITAM Wave 1 / F3 — financials, warranty & depreciation ───────────────── */

export const WARRANTY_META = {
  active: { label: 'In warranty', tone: 'success' },
  expiring: { label: 'Expiring', tone: 'warning' },
  expired: { label: 'Expired', tone: 'error' },
  unknown: { label: 'No warranty date', tone: 'default' },
};

export const DEPRECIATION_METHODS = [
  { value: 'straight_line', label: 'Straight line' },
];

/** Major-unit input string → integer minor units, or null when blank.
 *  Mirrors AssetFinancialsService.parseMoneyMinor; returns undefined when the
 *  input is not a usable amount so callers can show a field error. */
export const toMinor = (major) => {
  if (major == null || String(major).trim() === '') return null;
  const n = Number(String(major).replace(/[^0-9.-]/g, ''));
  if (!Number.isFinite(n)) return undefined;
  return Math.round(n * 100);
};

/** Integer minor units → the major-unit string an input field shows. */
export const toMajor = (minor) => (minor == null ? '' : String(Number(minor) / 100));

/** "45s" / "12m" / "3h 25m" / "2d 4h" from a seconds count. */
export const humanizeSeconds = (secs) => {
  const s = Number(secs) || 0;
  if (s < 60) return `${Math.round(s)}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 48) { const rem = m % 60; return rem ? `${h}h ${rem}m` : `${h}h`; }
  const d = Math.floor(h / 24);
  return `${d}d ${h % 24}h`;
};

/** Relative "x ago" for an ISO timestamp / epoch millis; empty string when absent. */
export const timeAgo = (when) => {
  if (!when) return '';
  const t = parseServerTs(when);
  if (!Number.isFinite(t)) return '';
  const m = Math.floor(Math.max(0, Date.now() - t) / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

/**
 * Activity classification for one software title against the usage map
 * (keys lower-cased). Contract: an entry with activeHosts > 0 is ACTIVE,
 * an entry with activeHosts = 0 is tracked-but-IDLE, no entry is UNTRACKED.
 */
export const activityOf = (name, usageLc) => {
  const u = usageLc ? usageLc[String(name || '').toLowerCase()] : undefined;
  if (!u) return { state: 'untracked', u: null };
  return { state: Number(u.activeHosts) > 0 ? 'active' : 'idle', u };
};

/** Quick-filter predicates for the chips row. */
export const matchesQuick = (row, key, usageLc) => {
  switch (key) {
    case 'shadow': return !!row.paid && !row.managed;
    case 'paid': return !!row.paid;
    case 'managed': return !!row.managed;
    case 'unclassified': return !row.managed && !row.paid;
    case 'idlePaid': return !!row.paid && activityOf(row.name, usageLc).state === 'idle';
    default: return true;
  }
};

// ---------------------------------------------------------------------------
// Usage quality (Scout v2 §3/§7.2). Weighted hours = focused×1.0 + cpu×0.5 +
// network×0.25. Usage tiers replace binary active/inactive, classified by a
// host×product's weighted hours over the window.
// ---------------------------------------------------------------------------

export const USAGE_WEIGHTS = { focused: 1.0, cpu: 0.5, network: 0.25 };

/** Weighted hours from a {focusedHours,cpuHours,networkHours} breakdown. */
export const weightedHours = (q) => {
  if (!q) return 0;
  return (Number(q.focusedHours) || 0) * USAGE_WEIGHTS.focused
    + (Number(q.cpuHours) || 0) * USAGE_WEIGHTS.cpu
    + (Number(q.networkHours) || 0) * USAGE_WEIGHTS.network;
};

/** Usage tier from weighted hours: power >20 / regular 1-20 / light <1>0 / inactive 0. */
export const usageTier = (wh) => {
  const h = Number(wh) || 0;
  if (h <= 0) return 'inactive';
  if (h < 1) return 'light';
  if (h <= 20) return 'regular';
  return 'power';
};

export const USAGE_TIER_META = {
  power: { label: 'Power', chipColor: 'success', hint: '> 20 weighted hours — fully utilised' },
  regular: { label: 'Regular', chipColor: 'info', hint: '1–20 weighted hours — utilised' },
  light: { label: 'Light', chipColor: 'warning', hint: '< 1 weighted hour — downgrade candidate' },
  inactive: { label: 'Inactive', chipColor: 'default', hint: '0 weighted hours — reclamation candidate' },
};

/** Order tier keys power → inactive for a breakdown bar / legend. */
export const USAGE_TIER_ORDER = ['power', 'regular', 'light', 'inactive'];

/** "3 power / 5 regular / 12 light / 80 inactive" from a {power,regular,light,inactive}. */
export const tierSummary = (b) =>
  USAGE_TIER_ORDER.filter((k) => Number(b?.[k]) > 0)
    .map((k) => `${Number(b[k])} ${k}`).join(' / ') || '—';

// Usage-trend rollup tiers (Scout v2 Mockup 7).
export const USAGE_TREND_TIERS = [
  { key: 'daily', label: '7 days' },
  { key: 'weekly', label: '13 weeks' },
  { key: 'monthly', label: '24 months' },
];

// ---------------------------------------------------------------------------
// Host lifecycle (Scout v2 §6): active (default) / stale (N days) / retired.
// The backend sets sys:lifecycle on HOST entities (hourly staleness sweep +
// operator Retire); we fall back to a last_seen derivation when it's absent.
// ---------------------------------------------------------------------------

export const STALE_AFTER_DAYS = 14;

/** Whole days since an ISO/space-separated timestamp; null when absent/invalid. */
export const daysSince = (when) => {
  if (!when || when === 'null') return null;
  const t = parseServerTs(when);
  if (!Number.isFinite(t)) return null;
  return Math.floor((Date.now() - t) / 86400000);
};

/**
 * Resolve a host's lifecycle: prefers the backend sys:lifecycle, else derives
 * from last_seen against the threshold. Returns { state, ageDays }.
 */
export const hostLifecycle = (h, thresholdDays = STALE_AFTER_DAYS) => {
  const ageDays = daysSince(h?.lastSeen);
  const explicit = String(h?.lifecycle || '').toLowerCase();
  if (explicit === 'retired') return { state: 'retired', ageDays };
  if (explicit === 'stale') return { state: 'stale', ageDays };
  if (explicit === 'active') {
    // Honour a fresh explicit "active" but still flag if last_seen has aged out.
    if (ageDays != null && ageDays > thresholdDays) return { state: 'stale', ageDays };
    return { state: 'active', ageDays };
  }
  if (ageDays == null) return { state: 'stale', ageDays };
  return { state: ageDays > thresholdDays ? 'stale' : 'active', ageDays };
};

export const LIFECYCLE_META = {
  active: { label: 'Active', chipColor: 'success' },
  stale: { label: 'Stale', chipColor: 'warning' },
  retired: { label: 'Retired', chipColor: 'default' },
};

/** Parse a tag JSON object (from the backend) defensively into {key: value}. */
export const parseTags = (raw) => {
  if (!raw || raw === 'null') return {};
  if (typeof raw === 'object') return raw;
  try {
    const o = JSON.parse(raw);
    return o && typeof o === 'object' ? o : {};
  } catch (e) {
    return {};
  }
};

/** Human status label matching the status chips in the table. */
export const statusLabel = (row) => {
  if (row.paid && !row.managed) return 'Shadow IT';
  const parts = [row.managed ? 'Managed' : null, row.paid ? 'Paid' : null].filter(Boolean);
  return parts.length ? parts.join(' + ') : 'Unclassified';
};

const csvCell = (v) => {
  const s = v == null ? '' : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

/** RFC-4180-ish CSV from a header array + array of row arrays. */
export const toCsv = (headers, rows) =>
  [headers, ...rows].map((r) => r.map(csvCell).join(',')).join('\n');

/** Client-side download of text content — no library needed. */
export const downloadText = (filename, text, mime = 'text/csv;charset=utf-8') => {
  const blob = new Blob([text], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

// ---------------------------------------------------------------------------
// Scheduled reports (ITAM Wave 1 — F5)
// ---------------------------------------------------------------------------

/** The report types, each rendered from the SAME query service that backs
 *  the matching on-screen table. `params` lists the filters that type honours. */
export const REPORT_TYPES = [
  { value: 'hosts_inventory', label: 'Hosts inventory', desc: 'Every host with OS, user, lifecycle and update posture.', params: ['posture'] },
  { value: 'license_position', label: 'Licence position', desc: 'Entitled vs consumed and the compliance position per licence.', params: ['groupId'] },
  { value: 'true_up', label: 'True-up', desc: 'Licence position plus estimated cost to cure.', params: ['groupId'] },
  { value: 'patch_posture', label: 'Patch posture', desc: 'Fleet bucket counts and the hosts needing attention.', params: [] },
  { value: 'financials_summary', label: 'Financials summary', desc: 'Purchase cost and book value by currency, warranty exposure.', params: ['groupId'] },
  { value: 'software_usage', label: 'Software usage', desc: 'Installed software with host and install counts.', params: [] },
  // Presence hours per host per window. "not reporting" means the host was not
  // observed — the report keeps it out of % office and shows % measured beside it.
  { value: 'host_presence', label: 'Device presence', desc: 'Office / remote hours per device and its user across time windows (e.g. 7, 30, 90 days).', params: ['windows'] },
];

export const reportTypeMeta = (t) =>
  REPORT_TYPES.find((r) => r.value === t) || { value: t, label: t || '—', desc: '', params: [] };

export const REPORT_SCHEDULES = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
];

export const REPORT_FORMATS = [
  { value: 'csv', label: 'CSV' },
  { value: 'pdf', label: 'PDF' },
];

export const WEEKDAYS = [
  { value: 1, label: 'Monday' }, { value: 2, label: 'Tuesday' }, { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' }, { value: 5, label: 'Friday' }, { value: 6, label: 'Saturday' },
  { value: 7, label: 'Sunday' },
];

export const RUN_STATUS_META = {
  success: { label: 'Success', tone: 'success' },
  running: { label: 'Running', tone: 'info' },
  failed: { label: 'Failed', tone: 'error' },
};

export const runStatusMeta = (s) => RUN_STATUS_META[s] || { label: s || 'Never run', tone: 'default' };

/** Human summary of a definition's cadence, e.g. "Monthly on day 1 at 06:00 UTC". */
export const scheduleSummary = (d) => {
  if (!d) return '—';
  const hh = `${String(d.anchorHour ?? 0).padStart(2, '0')}:00`;
  const tz = d.timezone || 'UTC';
  if (d.schedule === 'daily') return `Daily at ${hh} ${tz}`;
  if (d.schedule === 'weekly') {
    const day = WEEKDAYS.find((w) => w.value === Number(d.anchorDay))?.label || 'Monday';
    return `Weekly on ${day} at ${hh} ${tz}`;
  }
  return `Monthly on day ${d.anchorDay || 1} at ${hh} ${tz}`;
};

/** Delivery outcome is tracked separately from the run itself — a report that
 *  rendered is a success even when the mail server is down. */
export const DELIVERY_META = {
  sent: { label: 'Emailed', tone: 'success' },
  skipped: { label: 'No recipients', tone: 'default' },
  disabled: { label: 'Email off', tone: 'warning' },
  failed: { label: 'Delivery failed', tone: 'error' },
};

// ---------------------------------------------------------------------------
// Directory enrichment (ITAM Wave 1 — F6)
// ---------------------------------------------------------------------------

export const DIRECTORY_PROVIDERS = [
  { value: 'entra', label: 'Microsoft Entra ID', desc: 'App-only sync over Microsoft Graph.' },
  { value: 'csv', label: 'CSV import', desc: 'Credential-free fallback — upload an exported user list.' },
];

export const MATCHED_BY_META = {
  upn: { label: 'UPN', tone: 'success' },
  heuristic: { label: 'Domain match', tone: 'info' },
  manual: { label: 'Confirmed', tone: 'success' },
};

export const SYNC_STATUS_META = {
  success: { label: 'Synced', tone: 'success' },
  running: { label: 'Syncing', tone: 'info' },
  failed: { label: 'Failed', tone: 'error' },
};

/**
 * Canonical enrichment key for an observed username — MUST mirror the
 * backend's DirectoryService.hostUserKey exactly, or the lookup map misses.
 * A username that is already a UPN ignores the domain.
 */
export const directoryKey = (domain, username) => {
  const u = (username || '').trim();
  if (!u) return '';
  const d = (domain || '').trim();
  if (u.includes('@') || !d) return u.toLowerCase();
  return `${d}\\${u}`.toLowerCase();
};

/** Look an already-composed display string (e.g. "CORP\\jsmith") up in the map. */
export const directoryFor = (lookup, composed) => {
  if (!lookup || !composed) return null;
  return lookup[String(composed).toLowerCase()] || null;
};

// ---------------------------------------------------------------------------
// Server-side table state (ITAM pagination)
// ---------------------------------------------------------------------------

/** Debounce a value — used to keep a search box from firing a request per key. */
export const useDebounced = (value, delay = 300) => {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
};

const sizeKey = (storageKey) => `itam.rowsPerPage.${storageKey}`;

/** Rows-per-page remembered per table, so it survives navigating away and back. */
const storedPageSize = (storageKey, fallback) => {
  try {
    const raw = window.localStorage.getItem(sizeKey(storageKey));
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : fallback;
  } catch (e) {
    return fallback;
  }
};

/**
 * THE shared state for a server-paginated ITAM table.
 *
 * <p>Holds page / pageSize / sort / dir / search / arbitrary filters, and keeps
 * them in the URL query so a filtered page is shareable and survives a refresh.
 * The Inventory scene already lives under `?tab=`, so every write MERGES into
 * the existing params rather than replacing them — switching tabs still clears
 * the table state, which is what you want.
 *
 * <p>Two deliberate behaviours:
 * <ul>
 *   <li><b>The search box is local, the URL value is debounced.</b> Typing
 *       "workstation" would otherwise push eleven history entries and eleven
 *       requests. `q` is what you type; `debouncedQ` is what the server sees and
 *       what lands in the URL.</li>
 *   <li><b>Anything that changes the result SET resets to page 0.</b> Changing a
 *       filter while on page 40 of the old set would otherwise land you on an
 *       empty page.</li>
 * </ul>
 *
 * <p>Values equal to their default are omitted from the URL, so the common case
 * stays `?tab=hosts` rather than a wall of defaults.
 */
export const useTableState = ({
  storageKey,
  defaultSort,
  defaultDir = 'desc',
  defaultPageSize = 50,
  filters = {},              // { name: defaultValue }
} = {}) => {
  const [params, setParams] = useSearchParams();

  // An ABSENT param must fall back, not read as zero. `params.get()` returns
  // null when the key is missing and `Number(null)` is 0 — which is finite and
  // >= 0, so the old form silently answered 0 for every default. That made
  // `?tab=products` (no `size`) send pageSize=0; the server clamps <1 up to 1,
  // so every grid rendered exactly ONE row while the facet counts — computed
  // over the whole estate — kept showing the true totals.
  const num = (key, fallback) => {
    const raw = params.get(key);
    if (raw === null || raw.trim() === '') return fallback;
    const n = Number(raw);
    return Number.isFinite(n) && n >= 0 ? n : fallback;
  };

  const page = num('page', 0);
  const pageSize = num('size', storedPageSize(storageKey, defaultPageSize));
  const sort = params.get('sort') || defaultSort;
  const dir = params.get('dir') === 'asc' ? 'asc' : (params.get('dir') === 'desc' ? 'desc' : defaultDir);
  const urlQ = params.get('q') || '';

  const [q, setQ] = useState(urlQ);
  const debouncedQ = useDebounced(q, 300);

  const filterValues = {};
  Object.entries(filters).forEach(([k, dflt]) => { filterValues[k] = params.get(k) ?? dflt; });

  const defaults = useMemo(() => ({
    page: 0,
    size: storedPageSize(storageKey, defaultPageSize),
    sort: defaultSort,
    dir: defaultDir,
    q: '',
    ...filters,
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [storageKey, defaultSort, defaultDir, defaultPageSize, JSON.stringify(filters)]);

  const patch = useCallback((next, { keepPage = false } = {}) => {
    setParams((prev) => {
      const out = new URLSearchParams(prev);
      const apply = (k, v) => {
        const dflt = defaults[k];
        if (v === undefined || v === null || v === '' || String(v) === String(dflt)) out.delete(k);
        else out.set(k, String(v));
      };
      Object.entries(next).forEach(([k, v]) => apply(k, v));
      if (!keepPage && !('page' in next)) out.delete('page');
      return out;
    }, { replace: true });
  }, [setParams, defaults]);

  // Debounced search → URL (and therefore → the server). Resets to page 0.
  useEffect(() => {
    if (debouncedQ === urlQ) return;
    patch({ q: debouncedQ });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQ]);

  const setPage = useCallback((p) => patch({ page: p }, { keepPage: true }), [patch]);

  const setPageSize = useCallback((n) => {
    try { window.localStorage.setItem(sizeKey(storageKey), String(n)); } catch (e) { /* private mode */ }
    // `size` is written explicitly (not compared against the just-stored default)
    // so the URL always describes the page it produced.
    setParams((prev) => {
      const out = new URLSearchParams(prev);
      out.set('size', String(n));
      out.delete('page');
      return out;
    }, { replace: true });
  }, [setParams, storageKey]);

  /**
   * Column click → server re-query. Same column flips direction, a new column
   * starts descending; either way page resets, because row 51 of the old order
   * is meaningless in the new one.
   */
  const toggleSort = useCallback((key) => {
    if (key === sort) patch({ sort: key, dir: dir === 'asc' ? 'desc' : 'asc' });
    else patch({ sort: key, dir: 'desc' });
  }, [patch, sort, dir]);

  const setFilter = useCallback((key, value) => patch({ [key]: value }), [patch]);

  const reset = useCallback(() => {
    setQ('');
    patch({ q: '', ...Object.fromEntries(Object.keys(filters).map((k) => [k, filters[k]])) });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patch, JSON.stringify(filters)]);

  return {
    page, pageSize, sort, dir,
    q, debouncedQ, setQ,
    filters: filterValues,
    setPage, setPageSize, toggleSort, setFilter, reset,
    /** Query params for the request — omit nothing the server needs. */
    query: { page, pageSize, sort, dir, q: debouncedQ, ...filterValues },
  };
};

/** Pagination metadata off a paginated ITAM response, with safe fallbacks for a
 *  backend build that predates it (the tri-state loading contract is unchanged:
 *  undefined = loading, null = endpoint missing, data = loaded). */
export const pageMeta = (data, rows, fallbackPageSize) => ({
  total: Number.isFinite(Number(data?.total)) ? Number(data.total) : (rows?.length ?? 0),
  page: Number.isFinite(Number(data?.page)) ? Number(data.page) : 0,
  pageSize: Number.isFinite(Number(data?.pageSize)) ? Number(data.pageSize) : fallbackPageSize,
  hasMore: !!data?.hasMore,
  /** False when the backend ignored the paging params — the caller then knows
   *  it is holding the whole set and can hide the pager. */
  paginated: data != null && data.total !== undefined,
});

/**
 * Same contract as {@link useTableState}, but WITHOUT the URL.
 *
 * <p>Detail-page tables (a host's installed applications, its running software)
 * are nested inside a page that already owns the URL for its own tab state, and
 * two tables on one page would collide over `?page=`. They still get persisted
 * rows-per-page and a debounced search — only the shareability is dropped, which
 * is the right trade for a sub-table of a record you reached by id.
 */
export const useLocalTableState = ({
  storageKey, defaultSort, defaultDir = 'desc', defaultPageSize = 50,
} = {}) => {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSizeState] = useState(() => storedPageSize(storageKey, defaultPageSize));
  const [sort, setSort] = useState(defaultSort);
  const [dir, setDir] = useState(defaultDir);
  const [q, setQ] = useState('');
  const debouncedQ = useDebounced(q, 300);

  // Any change to the result SET restarts at page 0 — page 40 of the old set
  // is meaningless in the new one.
  useEffect(() => { setPage(0); }, [debouncedQ, sort, dir, pageSize]);

  const setPageSize = useCallback((n) => {
    try { window.localStorage.setItem(sizeKey(storageKey), String(n)); } catch (e) { /* private mode */ }
    setPageSizeState(n);
  }, [storageKey]);

  const toggleSort = useCallback((key) => {
    if (key === sort) setDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSort(key); setDir('desc'); }
  }, [sort]);

  const reset = useCallback(() => { setQ(''); setPage(0); }, []);

  return {
    page, pageSize, sort, dir, q, debouncedQ, setQ,
    setPage, setPageSize, toggleSort, reset,
    query: { page, pageSize, sort, dir, q: debouncedQ },
  };
};
